import { Component } from '@theme/component';

/**
 * @typedef {object} ArticleTocRefs
 * @property {HTMLAnchorElement[]} [links] - Sidebar jump links, one per heading.
 */

/**
 * Highlights the "In this article" link for the heading currently in view.
 * The active link gets `aria-current="location"`, which the block CSS styles.
 *
 * @extends {Component<ArticleTocRefs>}
 */
class ArticleToc extends Component {
  /** @type {AbortController | undefined} */
  #controller;

  /** @type {{ link: HTMLAnchorElement, heading: HTMLElement }[]} */
  #entries = [];

  #frame = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#setup();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#setup();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#teardown();
  }

  #setup() {
    this.#teardown();

    this.#entries = [];
    for (const link of this.refs.links ?? []) {
      const id = decodeURIComponent(new URL(link.href).hash.slice(1));
      const heading = id ? document.getElementById(id) : null;
      if (heading) this.#entries.push({ link, heading });
    }

    if (!this.#entries.length) return;

    this.#controller = new AbortController();
    const { signal } = this.#controller;

    window.addEventListener('scroll', this.#schedule, { passive: true, signal });
    window.addEventListener('resize', this.#schedule, { passive: true, signal });
    this.#update();
  }

  #teardown() {
    this.#controller?.abort();
    this.#controller = undefined;
    cancelAnimationFrame(this.#frame);
    this.#frame = 0;
  }

  #schedule = () => {
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      this.#update();
    });
  };

  #update() {
    const [first] = this.#entries;
    if (!first) return;

    // A heading counts as reached once it crosses its own scroll margin (header + gap).
    const threshold = parseFloat(getComputedStyle(first.heading).scrollMarginBlockStart) || 0;
    let active = first;

    for (const entry of this.#entries) {
      if (entry.heading.getBoundingClientRect().top - threshold <= 1) active = entry;
      else break;
    }

    for (const { link } of this.#entries) {
      if (link === active.link) link.setAttribute('aria-current', 'location');
      else link.removeAttribute('aria-current');
    }
  }
}

if (!customElements.get('cc-article-toc')) {
  customElements.define('cc-article-toc', ArticleToc);
}
